import { useCallback, useEffect, useMemo, useState } from "react";

import {
  normalizeTrendComparisonWindow,
  readTrendComparisonWeekdayAnchorFromSession,
  readTrendComparisonWindowFromSession,
  type StoreInsightsSharedDateWindowPeriod,
  type StoreInsightsTrendComparisonWindow,
  writeTrendComparisonWeekdayAnchorToSession,
  writeTrendComparisonWindowToSession,
} from "@/pages/store-insights/shared/storeInsightsSelectionSession";
import type { DateRange } from "@/services/StorePerformanceService";
import { formatStoreDateKey, getBrowserTimezone } from "@/utils/storeTime";

const DEFAULT_TREND_COMPARISON_WINDOW: StoreInsightsTrendComparisonWindow = "week_before";

const supportsDayBefore = (
  period: StoreInsightsSharedDateWindowPeriod,
  currentDateRange?: DateRange,
) => {
  if (period === "today" || period === "yesterday") {
    return true;
  }

  if (period !== "custom" || !currentDateRange) {
    return false;
  }

  const browserTimezone = getBrowserTimezone();
  return (
    formatStoreDateKey(currentDateRange.start, browserTimezone) ===
    formatStoreDateKey(currentDateRange.end, browserTimezone)
  );
};

export const normalizeTrendComparisonWindowForPeriod = (
  comparisonWindow: StoreInsightsTrendComparisonWindow,
  period: StoreInsightsSharedDateWindowPeriod,
  currentDateRange?: DateRange,
): StoreInsightsTrendComparisonWindow =>
  comparisonWindow === "day_before" && !supportsDayBefore(period, currentDateRange)
    ? DEFAULT_TREND_COMPARISON_WINDOW
    : comparisonWindow;

export const useStoreInsightsTrendComparison = (
  period: StoreInsightsSharedDateWindowPeriod,
  currentDateRange?: DateRange,
) => {
  const [anchorComparisonToWeekdays, setAnchorComparisonToWeekdays] = useState(
    () => readTrendComparisonWeekdayAnchorFromSession() ?? true,
  );
  const [storedComparisonWindow, setStoredComparisonWindow] =
    useState<StoreInsightsTrendComparisonWindow>(
      () => readTrendComparisonWindowFromSession() ?? DEFAULT_TREND_COMPARISON_WINDOW,
    );
  const comparisonWindow = useMemo(
    () => normalizeTrendComparisonWindowForPeriod(storedComparisonWindow, period, currentDateRange),
    [currentDateRange, period, storedComparisonWindow],
  );

  useEffect(() => {
    if (storedComparisonWindow !== comparisonWindow) {
      setStoredComparisonWindow(comparisonWindow);
    }
    writeTrendComparisonWindowToSession(comparisonWindow);
  }, [comparisonWindow, storedComparisonWindow]);

  useEffect(() => {
    writeTrendComparisonWeekdayAnchorToSession(anchorComparisonToWeekdays);
  }, [anchorComparisonToWeekdays]);

  const setComparisonWindow = useCallback(
    (nextValue: StoreInsightsTrendComparisonWindow) => {
      const normalizedValue = normalizeTrendComparisonWindow(nextValue);
      setStoredComparisonWindow(
        normalizedValue
          ? normalizeTrendComparisonWindowForPeriod(normalizedValue, period, currentDateRange)
          : DEFAULT_TREND_COMPARISON_WINDOW,
      );
    },
    [currentDateRange, period],
  );

  return {
    comparisonWindow,
    setComparisonWindow,
    anchorComparisonToWeekdays,
    setAnchorComparisonToWeekdays,
  };
};
